import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { GetStrategyVariance, RebalanceLots, ManualRebalanceLots, postManualRebalance } from '../../Redux/Actions/transactionService';
import { Box, Typography, Button, Radio, RadioGroup, FormControlLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, CircularProgress } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import SameAllocationOrders from '../../Component/Rebalancing/SameAllocationOrders';
import DifferentAllocationOrders from '../../Component/Rebalancing/DifferentAllocationOrders';
import Modal from '@mui/material/Modal';
import AutoRebalanceConfirmationModal from '../../Component/Rebalancing/AutoRebalanceConfirmationModal';
import AutoRebalanceWiseModal from '../../Component/Rebalancing/AutoRebalanceWiseModal';
import customSuccessAlert from '../../Alerts/customSuccessAlert';
import customFailAlert from '../../Alerts/customFailAlert';

function getInitials(name) {
  if (!name) return '';
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function formatCurrency(value) {
  if (value === undefined || value === null) return '-';

  const num = Number(value);
  if (isNaN(num)) return '-';

  const absNum = Math.abs(num);
  let formatted;

  if (absNum >= 1e12) {
    formatted = (num / 1e12).toFixed(2) + 'T';
  } else if (absNum >= 1e9) {
    formatted = (num / 1e9).toFixed(2) + 'B';
  } else if (absNum >= 1e6) {
    formatted = (num / 1e6).toFixed(2) + 'M';
  } else if (absNum >= 1e3) {
    formatted = (num / 1e3).toFixed(2) + 'K';
  } else {
    formatted = num.toFixed(2);
  }

  return `$${formatted}`;
}

const ManualRebalancePage = ({ setShowManual, investors = [], selectedInvestors = [] }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Only manage allocationType, orders, assetOptions
  const [allocationType, setAllocationType] = useState('same');
  const [orders, setOrders] = useState([
    { orderType: '', asset: '', allocation: '', limitPrice: '', limitPriceFocused: false }
  ]);
  const [differentAllocationOrders, setDifferentAllocationOrders] = useState([]); // For different allocation orders


  const [assetOptions, setAssetOptions] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedWashSale, setSelectedWashSale] = useState([]);
  const [investorFirstNames, setInvestorFirstNames] = useState([]); // Only first names
  const [investorOptions, setInvestorOptions] = useState([]); // All investors from API
  const [selectedInvestorOption, setSelectedInvestorOption] = useState('select_all'); // 'random' or 'select_all'
  const [displayInvestors, setDisplayInvestors] = useState([]); // Investors to show in SameAllocationOrders
  const [lotsPreview, setLotsPreview] = useState(null); // For previewing lots
  const [selectedWashTrades, setSelectedWashTrades] = useState({});
  const [userWiseConfirmModalOpen, setUserWiseConfirmModalOpen] = useState(false);
  const [washSaleSelections, setWashSaleSelections] = useState({});
  const [varianceData, setVarianceData] = useState([]); // Store GetStrategyVariance data
  const [filteredVarianceData, setFilteredVarianceData] = useState([]); // Filtered variance data for table
  const [assetAllocations, setAssetAllocations] = useState([]); // [{ symbol, allocation }]
  const [allocationError, setAllocationError] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [rebalancePayload, setRebalancePayload] = useState(null);
  const [showLotsModal, setShowLotsModal] = useState(false);
  const [showUserWiseModal, setShowUserWiseModal] = useState(false);
  const [isRebalancing, setIsRebalancing] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true); // Loading state for initial data fetch
  const [capturedOrderData, setCapturedOrderData] = useState(null); // Store order data when Next is clicked
  const [currentStep, setCurrentStep] = useState(0); // Track current modal step: 0 = main form, 1 = lots modal, 2 = user-wise modal

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsLoadingData(true);
        try {
          const result = await dispatch(GetStrategyVariance({ strategyId: id }));
          const data = result?.payload?.data || [];

          const assetSymbols = new Set();

          // Filter data to only include selected investors if selectedInvestors is provided and not empty
          let filteredData = data;


          if (selectedInvestors && selectedInvestors.length > 0) {
            const selectedInvestorIds = selectedInvestors.map(inv => inv.id);
            filteredData = data.filter(user =>
              selectedInvestorIds.includes(user?.userRole?.id)
            );
          }


          const investorObjs = [];
          filteredData.forEach(user => {
            (user.assetsVariance || []).forEach(assetObj => {
              if (assetObj.asset && assetObj.asset.symbol) {
                assetSymbols.add(assetObj.asset.symbol);
              }
            });
            // Build investor object with id and name
            const firstName = user?.userRole?.user?.userInfo?.firstName;
            const userRoleId = user?.userRole?.id;
            if (firstName && userRoleId) {
              investorObjs.push({ id: userRoleId, name: firstName });
            }
          });



          // Filter out assets with zero allocations from asset options
          const strategyBreakups = data[0]?.strategy?.breakups || [];
          const allocationMap = new Map();
          strategyBreakups.forEach(b => {
            if (b.asset && b.asset.symbol) {
              allocationMap.set(b.asset.symbol, b.proportion);
            }
          });

          // Only include assets with positive allocations in the dropdown
          const assetsWithAllocations = Array.from(assetSymbols).filter(symbol => {
            const allocation = allocationMap.has(symbol) ? allocationMap.get(symbol) : 0;
            return allocation > 0; // Only include assets with positive allocations
          });

          setAssetOptions(assetsWithAllocations.map(symbol => ({ value: symbol, label: symbol })));
          setInvestorFirstNames(investorObjs.map(obj => ({ name: obj.name })));
          setInvestorOptions(investorObjs);
          setVarianceData(filteredData); // Use filtered data
          setFilteredVarianceData(filteredData); // Initialize filtered data
          setDisplayInvestors(filteredData); // Set display investors to selected ones

          // Initialize orders from breakups if present and orders are empty
          if (data[0]?.strategy?.breakups && orders.length === 1 && !orders[0].asset) {
            // Use all available assets from all investors, not just the first one
            const allAssetSymbols = new Set();
            filteredData.forEach(user => {
              (user.assetsVariance || []).forEach(assetObj => {
                if (assetObj.asset && assetObj.asset.symbol) {
                  allAssetSymbols.add(assetObj.asset.symbol);
                }
              });
            });

            // Create orders for all available assets
            const allAssets = Array.from(allAssetSymbols);
            if (allAssets.length > 0) {
              // Use strategy breakups for allocation if available, otherwise use empty allocation
              const strategyBreakups = data[0]?.strategy?.breakups || [];
              const allocationMap = new Map();
              strategyBreakups.forEach(b => {
                if (b.asset && b.asset.symbol) {
                  allocationMap.set(b.asset.symbol, b.proportion);
                }
              });

              // Filter out assets with zero or empty allocations
              const assetsWithAllocations = allAssets.filter(symbol => {
                const allocation = allocationMap.has(symbol) ? allocationMap.get(symbol) : 0;
                return allocation > 0; // Only include assets with positive allocations
              });

              // Create orders only for assets with positive allocations
              setOrders(
                assetsWithAllocations.map(symbol => ({
                  orderType: '',
                  asset: symbol,
                  allocation: allocationMap.get(symbol),
                  limitPrice: '',
                  limitPriceFocused: false
                }))
              );
            }
          }

          // Initialize different allocation orders from breakups
          if (data[0]?.strategy?.breakups && filteredData.length > 0) {
            // Use all available assets from all investors, not just the first one
            const allAssetSymbols = new Set();
            filteredData.forEach(user => {
              (user.assetsVariance || []).forEach(assetObj => {
                if (assetObj.asset && assetObj.asset.symbol) {
                  allAssetSymbols.add(assetObj.asset.symbol);
                }
              });
            });

            const allAssets = Array.from(allAssetSymbols);
            if (allAssets.length > 0) {
              // Use strategy breakups for allocation if available, otherwise use empty allocation
              const strategyBreakups = data[0]?.strategy?.breakups || [];
              const allocationMap = new Map();
              strategyBreakups.forEach(b => {
                if (b.asset && b.asset.symbol) {
                  allocationMap.set(b.asset.symbol, b.proportion);
                }
              });

              // Filter out assets with zero or empty allocations
              const assetsWithAllocations = allAssets.filter(symbol => {
                const allocation = allocationMap.has(symbol) ? allocationMap.get(symbol) : 0;
                return allocation > 0; // Only include assets with positive allocations
              });

              const initialDifferentOrders = filteredData.map(investor => ({
                ...investor,
                orderType: '',
                orders: assetsWithAllocations.map(symbol => ({
                  orderType: '',
                  asset: symbol,
                  allocation: allocationMap.get(symbol),
                  limitPrice: '',
                  limitPriceFocused: false
                }))
              }));
              setDifferentAllocationOrders(initialDifferentOrders);
            }
          }
        } catch (err) {
          console.error('Error fetching strategy variance:', err);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchData();
  }, [dispatch, id, selectedInvestors]);

  const handleOrderFieldChange = (idx, field, value) => {
    setOrders(prev =>
      prev.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      )
    );
  };

  const handleDifferentOrderFieldChange = (investorIndex, orderIndex, field, value) => {
    setDifferentAllocationOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];

      if (currentInvestor.orders && currentInvestor.orders[orderIndex]) {
        currentInvestor.orders[orderIndex] = {
          ...currentInvestor.orders[orderIndex],
          [field]: value
        };
      }

      return updatedInvestors;
    });
  };

  // Handle investor changes from SameAllocationOrders
  const handleInvestorsChange = (updatedInvestors) => {
    setDisplayInvestors(updatedInvestors);

    // Filter varianceData to only include the remaining investors
    const remainingInvestorIds = updatedInvestors.map(inv => inv.userRole?.id);
    const filtered = varianceData.filter(user =>
      remainingInvestorIds.includes(user?.userRole?.id)
    );
    setFilteredVarianceData(filtered);

    // Recalculate assetOptions based on REMAINING investors only
    // This ensures the dropdown shows only assets from selected investors
    const assetSymbols = new Set();
    filtered.forEach(user => {
      (user.assetsVariance || []).forEach(assetObj => {
        if (assetObj.asset && assetObj.asset.symbol) {
          assetSymbols.add(assetObj.asset.symbol);
        }
      });
    });

    // Filter out assets with zero allocations from asset options
    const strategyBreakups = varianceData[0]?.strategy?.breakups || [];
    const allocationMap = new Map();
    strategyBreakups.forEach(b => {
      if (b.asset && b.asset.symbol) {
        allocationMap.set(b.asset.symbol, b.proportion);
      }
    });

    // Only include assets with positive allocations in the dropdown
    const assetsWithAllocations = Array.from(assetSymbols).filter(symbol => {
      const allocation = allocationMap.has(symbol) ? allocationMap.get(symbol) : 0;
      return allocation > 0; // Only include assets with positive allocations
    });

    const newAssetOptions = assetsWithAllocations.map(symbol => ({ value: symbol, label: symbol }));
    setAssetOptions(newAssetOptions);

    // Clear orders that reference assets no longer available
    const availableAssetSymbols = new Set(newAssetOptions.map(option => option.value));
    setOrders(prev => prev.filter(order =>
      !order.asset || availableAssetSymbols.has(order.asset)
    ));

    // If no orders remain, add a default empty order
    setOrders(prev => {
      if (prev.length === 0) {
        return [{ orderType: '', asset: '', allocation: '', limitPrice: '', limitPriceFocused: false }];
      }
      return prev;
    });
  };

  const handleAllocationTypeChange = (e) => {
    const newAllocationType = e.target.value;
    setAllocationType(newAllocationType);

    // Reset and properly initialize data when switching between allocation types
    if (newAllocationType === 'same') {
      // When switching to same allocation, use current asset options
      if (assetOptions.length > 0) {
        // Use strategy breakups for allocation if available, otherwise use empty allocation
        const strategyBreakups = varianceData[0]?.strategy?.breakups || [];
        const allocationMap = new Map();
        strategyBreakups.forEach(b => {
          if (b.asset && b.asset.symbol) {
            allocationMap.set(b.asset.symbol, b.proportion);
          }
        });

        // Filter out assets with zero or empty allocations
        const assetsWithAllocations = assetOptions.filter(option => {
          const allocation = allocationMap.has(option.value) ? allocationMap.get(option.value) : 0;
          return allocation > 0; // Only include assets with positive allocations
        });

        // Initialize with current asset options that have positive allocations
        setOrders(
          assetsWithAllocations.map(option => ({
            orderType: '',
            asset: option.value,
            allocation: allocationMap.get(option.value),
            limitPrice: '',
            limitPriceFocused: false
          }))
        );
      } else {
        // Initialize with empty orders
        setOrders([
          { orderType: '', asset: '', allocation: '', limitPrice: '', limitPriceFocused: false }
        ]);
      }

      // Reset allocation error and can proceed state
      setAllocationError(false);
      setCanProceed(false);

    } else if (newAllocationType === 'different') {
      // When switching to different allocation, use current asset options
      if (displayInvestors.length > 0 && assetOptions.length > 0) {
        // Use strategy breakups for allocation if available, otherwise use empty allocation
        const strategyBreakups = varianceData[0]?.strategy?.breakups || [];
        const allocationMap = new Map();
        strategyBreakups.forEach(b => {
          if (b.asset && b.asset.symbol) {
            allocationMap.set(b.asset.symbol, b.proportion);
          }
        });

        // Filter out assets with zero or empty allocations
        const assetsWithAllocations = assetOptions.filter(option => {
          const allocation = allocationMap.has(option.value) ? allocationMap.get(option.value) : 0;
          return allocation > 0; // Only include assets with positive allocations
        });

        // Initialize with current asset options that have positive allocations
        const initialDifferentOrders = displayInvestors.map(investor => ({
          ...investor,
          orderType: '',
          orders: assetsWithAllocations.map(option => ({
            orderType: '',
            asset: option.value,
            allocation: allocationMap.get(option.value),
            limitPrice: '',
            limitPriceFocused: false
          }))
        }));
        setDifferentAllocationOrders(initialDifferentOrders);
      } else {
        // Initialize with empty orders
        const initialDifferentOrders = displayInvestors.map(investor => ({
          ...investor,
          orderType: '',
          orders: [
            { orderType: '', asset: '', allocation: '', limitPrice: '', limitPriceFocused: false }
          ]
        }));
        setDifferentAllocationOrders(initialDifferentOrders);
      }

      // Reset allocation error and can proceed state
      setAllocationError(false);
      setCanProceed(false);
    }
  };

  const handleConfirmRebalance = () => {
    const strategyId = varianceData[0]?.strategy?.id || '';

    if (allocationType === 'same') {
      // Handle same allocation
      const type = orders[0]?.orderType === 'LMT' ? 'LMT' : 'MKT';

      // Build orderItems only for LMT orders (only for positive allocations)
      const positiveOrders = orders.filter(o => Number(o.allocation) > 0);
      const orderItems = type === 'LMT'
        ? positiveOrders.map(order => ({
            symbol: order.asset,
            price: Number(order.limitPrice),
            isWashTrade: false
          }))
        : [];

      // Build userRoleAssets for all investors with proper wash trade detection
      const userRoleAssets = displayInvestors.map(inv => {
        // Find the user's variance data to get wash trade info
        const userVarianceData = varianceData.find(user => user.userRole?.id === inv.userRole?.id);

        return {
          userRoleId: inv.userRole?.id,
          assets: positiveOrders.map(order => {
            // Find if this asset has wash trade data for this user
            const assetVariance = userVarianceData?.assetsVariance?.find(asset =>
              asset.asset?.name === order.asset
            );

            return {
              symbol: order.asset,
              isWash: assetVariance?.isWashTrade === false,
              proportion: Number(order.allocation)
            };
          })
        };
      });

      const payload = [{
        type,
        ...(type === 'LMT' && { orderItems }),
        strategyId,
        userRoleAssets
      }];

      setRebalancePayload(payload);
      setShowLotsModal(true);
    } else if (allocationType === 'different') {
      // Handle different allocation
      const type = differentAllocationOrders[0]?.orderType === 'LMT' ? 'LMT' : 'MKT';

      // Build orderItems only for LMT (use first investor's orders as template, only positive allocations)
      const templateOrders = (differentAllocationOrders[0]?.orders || []).filter(o => Number(o.allocation) > 0);
      const orderItems = type === 'LMT'
        ? templateOrders.map(order => ({
            symbol: order.asset,
            price: Number(order.limitPrice),
            isWashTrade: false
          }))
        : [];

      // Build userRoleAssets for each investor with their specific allocations (only positive allocations)
      const userRoleAssets = differentAllocationOrders.map(inv => {
        // Find the user's variance data to get wash trade info
        const userVarianceData = varianceData.find(user => user.userRole?.id === inv.userRole?.id);

        return {
          userRoleId: inv.userRole?.id,
          assets: (inv.orders || []).filter(o => Number(o.allocation) > 0).map(order => {
            // Find if this asset has wash trade data for this user
            const assetVariance = userVarianceData?.assetsVariance?.find(asset =>
              asset.asset?.name === order.asset
            );

            return {
              symbol: order.asset,
              isWash: assetVariance?.isWashTrade === false,
              proportion: Number(order.allocation)
            };
          })
        };
      });

      const payload = [{
        type,
        ...(type === 'LMT' && { orderItems }),
        strategyId,
        userRoleAssets
      }];

      setRebalancePayload(payload);
      setShowLotsModal(true);
    }
  };

  // Replace handleNextClick to call handleConfirmRebalance
  const handleNextClick = async () => {
    if (canProceed) {
      // Capture order data when Next is clicked
      const orderData = {
        allocationType: allocationType,
        orders: allocationType === 'same' ? [...orders] : null,
        differentAllocationOrders: allocationType === 'different' ? [...differentAllocationOrders] : null
      };
      setCapturedOrderData(orderData);
      setCurrentStep(1);
      await handlePreviewLots(); // <-- This will fetch and set lotsPreview
      handleConfirmRebalance();  // <-- This will open the modal
    }
  };
  
 
  const handleWashSaleCheck = (id) => {
    setSelectedWashSale(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };



  // Function to preview lots using RebalanceLots API
  const handlePreviewLots = async () => {
    const userRoleIds = displayInvestors.map(inv => inv.userRole?.id).filter(Boolean); // Use displayInvestors instead of investorOptions
    if (!id || userRoleIds.length === 0) return;
    const body = {
      strategyId: id,
      userRoleIds
    };
    try {
      const response = await dispatch(RebalanceLots({ body }));
      const lotsData = response?.payload?.data;
      setLotsPreview(lotsData); // You can use this state to display in your UI
    } catch (error) {
      console.error('Error previewing lots:', error);
    }
  };



  const handleWashSaleCheckbox = (key) => {
    setWashSaleSelections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper: check if allocations sum to 100 and all assets are unique, but only show red border after all allocations are filled
  const hasAllocationOrAssetError = () => {
    // If all allocations are empty (initial state), don't show border
    const allAllocationsEmpty = orders.every(order => !order.allocation && order.allocation !== 0);
    if (allAllocationsEmpty) return false;

    // If any allocation is empty, don't show border
    const allAllocationsFilled = orders.every(order => order.allocation !== '' && order.allocation !== null && order.allocation !== undefined);
    if (!allAllocationsFilled) return false;

    // All allocations filled, now check for errors
    const allocationSum = orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    const assets = orders.map(order => order.asset);
    const allAssetsSelected = assets.every(asset => asset);
    const assetsUnique = new Set(assets).size === assets.length;
    return allocationSum !== 100 || !assetsUnique || !allAssetsSelected;
  };

  // Helper: check if different allocation orders have valid allocations
  const hasDifferentAllocationError = () => {
    if (allocationType !== 'different' || differentAllocationOrders.length === 0) return false;

    return differentAllocationOrders.some(investor => {
      const orders = investor.orders || [];
      const allocationSum = orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
      return allocationSum !== 100;
    });
  };

  // Helper: get specific error message for the order list
  const getOrderListErrorMessage = () => {
    const allAllocationsFilled = orders.every(order => order.allocation !== '' && order.allocation !== null && order.allocation !== undefined);
    if (!allAllocationsFilled) return "";
    const allocationSum = orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    if (allocationSum !== 100) return "Error: The total allocation should equal 100%";
    return "";
  };

  // Helper: sum allocations
  const allocationSum = assetAllocations?.reduce((sum, a) => sum + Number(a.allocation), 0);

  // Check if can proceed based on allocation type
  const canProceedForSameAllocation = () => {
    if (allocationType !== 'same') return false;
    const allocationSum = orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    const hasValidOrders = orders.length > 0 && orders.every(order =>
      order.asset && order.asset.trim() !== '' &&
      order.allocation !== '' && order.allocation !== null && order.allocation !== undefined &&
      !isNaN(Number(order.allocation))
    );

    // Check if current order type is Limit and all limit prices are filled and positive
    const currentOrderType = orders[0]?.orderType || '';
    const isLimit = currentOrderType === 'LMT';
    const allLimitPricesFilled = isLimit
      ? orders.every(order => order.limitPrice && order.limitPrice.trim() !== '' && !isNaN(Number(order.limitPrice)) && Number(order.limitPrice) > 0)
      : true;

    return allocationSum === 100 && hasValidOrders && currentOrderType && (currentOrderType === 'MKT' || (isLimit && allLimitPricesFilled));
  };

  const canProceedForDifferentAllocation = () => {
    if (allocationType !== 'different') return false;
    
    // Check if differentAllocationOrders is empty or not properly initialized
    if (!differentAllocationOrders || differentAllocationOrders.length === 0) return false;
    
    // First check if all investors have selected an order type
    const allInvestorsHaveOrderType = differentAllocationOrders.every(investor => 
      investor.orderType && investor.orderType.trim() !== ''
    );
    
    if (!allInvestorsHaveOrderType) return false;
    
    return differentAllocationOrders.every(investor => {
      const orders = investor.orders || [];
      const allocationSum = orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
      const hasValidOrders = orders.length > 0 && orders.every(order =>
        order.asset && order.asset.trim() !== '' &&
        order.allocation !== '' && order.allocation !== null && order.allocation !== undefined &&
        !isNaN(Number(order.allocation))
      );

      // Check if current order type is Limit and all limit prices are filled and positive
      const currentOrderType = investor.orderType || '';
      const isLimit = currentOrderType === 'LMT';
      const allLimitPricesFilled = isLimit
        ? orders.every(order => order.limitPrice && order.limitPrice.trim() !== '' && !isNaN(Number(order.limitPrice)) && Number(order.limitPrice) > 0)
        : true;

      return allocationSum === 100 && hasValidOrders && currentOrderType && (currentOrderType === 'MKT' || (isLimit && allLimitPricesFilled));
    });
  };

  const isNextEnabled = () => {
    if (allocationType === 'same') {
      return canProceedForSameAllocation();
    } else if (allocationType === 'different') {
      return canProceedForDifferentAllocation();
    }
    return false;
  };

  // Callback for SameAllocationOrders
  const handleAllocationsChange = (allocs) => {
    setAssetAllocations(allocs);
  };

  // Update canProceed based on current allocation type
  useEffect(() => {
    const newCanProceed = isNextEnabled();
    setCanProceed(newCanProceed);
  }, [allocationType, orders, differentAllocationOrders]);


  // Helper to transform lotsPreview to rebalanceData format for the modal
  const getRebalanceTableData = () => {
    if (!lotsPreview) return [];
    let rows = [];
    Object.entries(lotsPreview).forEach(([userId, lots], idx) => {
     
      const userObj = Array.isArray(varianceData) ? varianceData.find(u => u.userRole?.id === userId)
        : null;
      const investorName = userObj?.userRole?.user?.userInfo?.firstName || "-";
      // Create a unique identifier for this investor to ensure proper grouping
      const uniqueInvestorId = `${investorName}_${userId}`;

      lots.forEach(lot => {
        rows.push({
          investor: investorName,
          uniqueInvestorId: uniqueInvestorId, // Add unique identifier for proper grouping
          asset: lot.asset?.name || '-',
          symbol: lot.asset?.symbol || '-',
          shareCount: lot.shareCount !== undefined ? Number(lot.shareCount).toFixed(2) : '-',
          sellQuantity: lot.sellQuantity !== undefined ? Number(lot.sellQuantity).toFixed(2) : '-',
          daysHeld: lot.daysHeld !== undefined ? lot.daysHeld : '-',
          costPrice: lot.costPrice !== undefined ? Number(lot.costPrice).toFixed(2) : '-',
          marketPrice: lot.marketPrice !== undefined ? Number(lot.marketPrice).toFixed(5) : '-',
          totalProfit: lot.totalProfit !== undefined ? Number(lot.totalProfit).toFixed(2) : '0'
        });
      });
    });
    return rows;
  };

  const handleLotsModalClose = () => {
    // Go back to main form (step 0)
    setCurrentStep(0);
    setShowLotsModal(false);
  };

  const handleLotsModalConfirm = () => {
    // Move to next step (user-wise modal)
    setCurrentStep(2);
    setShowLotsModal(false);
    // Update filtered data based on current displayInvestors before opening the modal
    const remainingInvestorIds = displayInvestors.map(inv => inv.userRole?.id);
    const filtered = varianceData.filter(user =>
      remainingInvestorIds.includes(user?.userRole?.id)
    );
    setFilteredVarianceData(filtered);
    setShowUserWiseModal(true);
  };

  const handleUserWiseModalClose = () => {
    // Go back to lots modal (step 1)
    setCurrentStep(1);
    setShowUserWiseModal(false);
    setWashSaleSelections({}); // Reset wash sale selections
    setShowLotsModal(true);
  };

  const handleUserWiseModalConfirm = async () => {
 
    setIsRebalancing(true);
    try {
      // Get strategy ID from variance data
      const strategyId = varianceData[0]?.strategy?.id || '';

      if (allocationType === 'same') {
        // Handle same allocation
        const type = orders[0]?.orderType === 'LMT' ? 'LMT' : 'MKT';

        // Build orderItems array only for LMT orders (only for positive allocations)
        const positiveOrders = orders.filter(o => Number(o.allocation) > 0);
        const orderItems = type === 'LMT' ? positiveOrders.map(order => {
          const baseItem = {
            symbol: order.asset,
            price: Number(order.limitPrice),
            isWashTrade: false
          };
          return baseItem;
        }) : [];

        // Build userRoleAssets array
        const userRoleAssets = displayInvestors.map(inv => {
          const userRoleId = inv.userRole?.id;

          // Find the user's variance data to get wash trade info
          const userVarianceData = varianceData.find(user => user.userRole?.id === userRoleId);

          const assets = positiveOrders.map(order => {
            // Find if this asset has wash trade data for this user
            const assetVariance = userVarianceData?.assetsVariance?.find(asset =>
              asset.asset?.name === order.asset
            );

            // Check if this specific user-asset combination is selected for wash sale
            const key = `${userRoleId}-${order.asset}`;
            const isWashSelected = washSaleSelections[key] || false;

            return {
              symbol: order.asset,
              isWash: isWashSelected,
              proportion: Number(order.allocation)
            };
          });

          return {
            userRoleId: userRoleId,
            assets: assets
          };
        });

        // Construct the final payload
        const payload = [{
          type: type,
          ...(type === 'LMT' && { orderItems: orderItems }),
          strategyId: strategyId,
          userRoleAssets: userRoleAssets
        }];

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 30000); // 30 second timeout
        });
        
        const apiPromise = dispatch(postManualRebalance({ body: payload }));
        const response = await Promise.race([apiPromise, timeoutPromise]);

        const statusCode = response?.payload?.statusCode;
        if (statusCode === 201) {
          customSuccessAlert('Rebalance completed successfully!').then((result) => {
            if (result.isConfirmed) {
              navigate(-1);
            }
          });
          
        } else {
           customFailAlert(response?.payload?.message || 'Rebalance failed.');
        }
             } else if (allocationType === 'different') {
         // Handle different allocation - Create separate payload for each investor
         const payload = [];
         
         differentAllocationOrders.forEach(investor => {
           const orderType = investor.orderType;
           if (orderType) {
             const type = orderType === 'LMT' ? 'LMT' : 'MKT';
             
             // Build orderItems array only for LMT orders (use this investor's orders as template, only positive allocations)
             const templateOrders = (investor.orders || []).filter(o => Number(o.allocation) > 0);
             const orderItems = type === 'LMT' ? templateOrders.map(order => {
               const baseItem = {
                 symbol: order.asset,
                 price: Number(order.limitPrice),
                 isWashTrade: false
               };
               return baseItem;
             }) : [];

             // Build userRoleAssets array for this specific investor only
             const userRoleId = investor.userRole?.id;

             // Find the user's variance data to get wash trade info
             const userVarianceData = varianceData.find(user => user.userRole?.id === userRoleId);

             const assets = (investor.orders || []).filter(o => Number(o.allocation) > 0).map(order => {
               // Find if this asset has wash trade data for this user
               const assetVariance = userVarianceData?.assetsVariance?.find(asset =>
                 asset.asset?.name === order.asset
               );

               // Check if this specific user-asset combination is selected for wash sale
               const key = `${userRoleId}-${order.asset}`;
               const isWashSelected = washSaleSelections[key] || false;

               return {
                 symbol: order.asset,
                 isWash: isWashSelected,
                 proportion: Number(order.allocation)
               };
             });

             const userRoleAssets = [{
               userRoleId: userRoleId,
               assets: assets
             }];

             // Add payload for this individual investor
             payload.push({
               type: type,
               ...(type === 'LMT' && { orderItems: orderItems }),
               strategyId: strategyId,
               userRoleAssets: userRoleAssets
             });
           }
         });

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 30000); // 30 second timeout
        });
        
        const apiPromise = dispatch(postManualRebalance({ body: payload }));
        const response = await Promise.race([apiPromise, timeoutPromise]);

        const statusCode = response?.payload?.statusCode;
        if (statusCode === 201) {
           customSuccessAlert('Rebalance completed successfully!').then((result) => {
             if (result.isConfirmed) {
               navigate(-1); // Navigate back to previous page
             }
           });
           
        } else {
           customFailAlert(response?.payload?.message || 'Rebalance failed.');
        }
      }
    } catch (err) {
      console.error('Error during rebalance:', err);
      
      // Show specific error message for timeout
      if (err.message === 'Request timeout') {
        customFailAlert('Request timed out. Please try again or check your connection.');
      } else {
        customFailAlert('Rebalance failed. Please try again.');
      }
    } finally {
      setIsRebalancing(false);
      setShowUserWiseModal(false);
      setCurrentStep(0); // Reset to main form
    }
    
   //working now...
    // setWashSaleSelections({}); // Reset wash sale selections
  };

  // Get strategy name from varianceData
  const strategyName = varianceData[0]?.strategy?.name || '';

  // Function to get allocation for a specific asset and user from captured order data
  const getAllocationForAsset = (userData, assetSymbol) => {
    if (!capturedOrderData) return 0;
    
    if (capturedOrderData.allocationType === 'same') {
      // For same allocation, find the allocation from captured orders
      const order = capturedOrderData.orders?.find(o => o.asset === assetSymbol);
      return order ? parseFloat(order.allocation) || 0 : 0;
    } else if (capturedOrderData.allocationType === 'different') {
      // For different allocation, find the specific investor's allocation
      const investorOrder = capturedOrderData.differentAllocationOrders?.find(inv => 
        inv.userRole?.id === userData.userRole?.id
      );
      
      if (investorOrder && investorOrder.orders) {
        const order = investorOrder.orders.find(o => o.asset === assetSymbol);
        return order ? parseFloat(order.allocation) || 0 : 0;
      }
    }
    return 0;
  };

  // Function to get limit price for a specific asset and user from captured order data
  const getLimitPriceForAsset = (userData, assetSymbol) => {
    if (!capturedOrderData) return null;
    
    if (capturedOrderData.allocationType === 'same') {
      // For same allocation, find the limit price from captured orders
      const order = capturedOrderData.orders?.find(o => o.asset === assetSymbol);
      return order?.limitPrice || null;
    } else if (capturedOrderData.allocationType === 'different') {
      // For different allocation, find the specific investor's limit price
      const investorOrder = capturedOrderData.differentAllocationOrders?.find(inv => 
        inv.userRole?.id === userData.userRole?.id
      );
      
      if (investorOrder && investorOrder.orders) {
        const order = investorOrder.orders.find(o => o.asset === assetSymbol);
        return order?.limitPrice || null;
      }
    }
    return null;
  };

  // Function to check if order type is Limit for a specific user
  const isLimitOrderType = (userData) => {
    if (!capturedOrderData) return false;
    
    if (capturedOrderData.allocationType === 'same') {
      // For same allocation, check the first order's type
      return capturedOrderData.orders?.[0]?.orderType === 'LMT';
    } else if (capturedOrderData.allocationType === 'different') {
      // For different allocation, find the specific investor's order type
      const investorOrder = capturedOrderData.differentAllocationOrders?.find(inv => 
        inv.userRole?.id === userData.userRole?.id
      );
      return investorOrder?.orderType === 'LMT';
    }
    return false;
  };

  // Don't render the main content until data is loaded
  if (isLoadingData) {
    return (
      <Box sx={{ p: 4, background: '#fafbfc', minHeight: '100vh' }}>
        {/* Heading Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setShowManual(false)} sx={{ mr: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Manual Rebalance
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, background: '#fafbfc', minHeight: '100vh' }}>
      {/* Heading Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => setShowManual(false)} sx={{ mr: 1 }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Manual Rebalance <span style={{ fontWeight: 'bold' }}> | {strategyName}</span>
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={!canProceed}
          onClick={handleNextClick}
          sx={{
            background: canProceed ? '#1976d2' : '#e0e0e0',
            color: canProceed ? '#fff' : '#bdbdbd',
            boxShadow: 'none',
            fontWeight: 500,
            fontSize: 16,
            borderRadius: 2,
            px: 4,
            py: 1,
            minWidth: 90,
            '&:hover': {
              background: canProceed ? '#1565c0' : '#e0e0e0',
            },
          }}
        >
          Next
        </Button>
      </Box>
      {/* Allocation Type */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <RadioGroup
          row
          value={allocationType}
          onChange={handleAllocationTypeChange}
        >
          <FormControlLabel value="same" control={<Radio color="primary" />} label="Same Allocation" />
          <FormControlLabel value="different" control={<Radio color="primary" />} label="Different Allocation" />
        </RadioGroup>
      </Box>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {allocationType === 'same' && (
          <SameAllocationOrders
            key={`same-${allocationType}`}
            orders={orders}
            assetOptions={assetOptions}
            handleOrderFieldChange={handleOrderFieldChange}
            setOrders={setOrders}
            // investors={displayInvestors.map(inv => ({ name: inv.name }))}
            investors={displayInvestors}
            loading={isLoadingData}
            getInitials={getInitials}
            error={hasAllocationOrAssetError()}
            errorMessage={getOrderListErrorMessage()}
            onAllocationError={setAllocationError}
            onCanProceedChange={setCanProceed}
            onInvestorsChange={handleInvestorsChange}
          />
        )}
        {allocationType === 'different' && (
          <DifferentAllocationOrders
            key={`different-${allocationType}`}
            investorOrders={differentAllocationOrders}
            assetOptions={assetOptions}
            handleInvestorFieldChange={handleDifferentOrderFieldChange}
            handleOpenRemoveModal={() => { }}
            removeModalOpen={false}
            handleCloseRemoveModal={() => { }}
            investorToRemove={null}

            strategyBreakups={varianceData[0]?.strategy?.breakups}
            setInvestorOrders={setDifferentAllocationOrders}
            onAllocationError={setAllocationError}
            onCanProceedChange={setCanProceed}
            loading={isLoadingData}
          />
        )}
      </Box>

      {/* Confirm User-Wise Rebalance Modal */}
      <AutoRebalanceConfirmationModal
        open={showLotsModal}
        onClose={handleLotsModalClose}
        rebalanceData={getRebalanceTableData()}
        onConfirm={handleLotsModalConfirm}
        strategyName={strategyName}
        orders={capturedOrderData ? (capturedOrderData.allocationType === 'same' ? capturedOrderData.orders : capturedOrderData.differentAllocationOrders[0]?.orders) : []}
        currentStep={currentStep}
        onStepBack={() => {
          setCurrentStep(0);
          setShowLotsModal(false);
        }}
      />



      {/* Final user-wise modal */}
      <AutoRebalanceWiseModal
        open={showUserWiseModal}
        onClose={handleUserWiseModalClose}
        onConfirm={handleUserWiseModalConfirm}
        strategyName={strategyName}
        filteredVarianceData={filteredVarianceData}
        varianceData={varianceData}
        orders={capturedOrderData ? (capturedOrderData.allocationType === 'same' ? capturedOrderData.orders : capturedOrderData.differentAllocationOrders[0]?.orders) : []}
        isRebalancing={isRebalancing}
        currentStep={currentStep}
        onStepBack={() => {
          setCurrentStep(1);
          setShowUserWiseModal(false);
          setShowLotsModal(true);
        }}
      />
    </Box>
  );
};

export default ManualRebalancePage; 